// ─── Digest week-window math (Friday 9:30 AM PT anchor) ──────────────────────
// Pure date logic shared by the server weekly-digest sender AND the client
// changelog page, so both bucket entries into the EXACT SAME Friday→Friday
// windows. The digest checks every Friday at 9:30 AM Pacific; a window ENDS on
// that Friday cutoff. No server-only imports — safe to use in the browser.

export const DIGEST_TZ = "America/Los_Angeles";
const DIGEST_WEEKDAY = 5; // Friday (Sun=0..Sat=6)
const DIGEST_HOUR = 9;
const DIGEST_MINUTE = 30;

export interface WeekRange {
  start: Date; // window start: a Friday 9:30 AM PT (inclusive)
  end: Date; // window end: the following Friday 9:30 AM PT (exclusive)
  weekStartIso: string; // YYYY-MM-DD (PT date) of `start` — the idempotency marker value
}

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function wallClockParts(d: Date, tz: string) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    weekday: "short",
  });
  const parts: Record<string, string> = {};
  for (const p of dtf.formatToParts(d)) parts[p.type] = p.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    weekday: WEEKDAY_NAMES.indexOf(parts.weekday),
  };
}

// Convert a wall-clock time in `tz` to the UTC instant, DST-aware.
// (Iterative refinement; converges in ≤3 passes.)
function zonedTimeToUtc(
  year: number,
  month: number, // 1-based
  day: number, // may be out of range; Date.UTC normalizes
  hour: number,
  minute: number,
  tz: string,
): Date {
  const desired = Date.UTC(year, month - 1, day, hour, minute);
  let ts = desired;
  for (let i = 0; i < 3; i++) {
    const p = wallClockParts(new Date(ts), tz);
    const wall = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    if (wall === desired) break;
    ts += desired - wall;
  }
  return new Date(ts);
}

// The most recent Friday 9:30 AM PT at or before `now`.
function lastDigestAnchor(now: Date): Date {
  const p = wallClockParts(now, DIGEST_TZ);
  const daysBack = (p.weekday - DIGEST_WEEKDAY + 7) % 7;
  let anchor = zonedTimeToUtc(
    p.year,
    p.month,
    p.day - daysBack,
    DIGEST_HOUR,
    DIGEST_MINUTE,
    DIGEST_TZ,
  );
  if (anchor.getTime() > now.getTime()) {
    // It's Friday, but before 9:30 AM PT — use last week's anchor.
    anchor = zonedTimeToUtc(
      p.year,
      p.month,
      p.day - daysBack - 7,
      DIGEST_HOUR,
      DIGEST_MINUTE,
      DIGEST_TZ,
    );
  }
  return anchor;
}

export function isoDateInTz(d: Date, tz: string = DIGEST_TZ): string {
  const p = wallClockParts(d, tz);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

// Shift an anchor (a Friday 9:30 AM PT instant) by N whole days, staying on the
// 9:30 AM PT wall clock and DST-correct.
function shiftAnchor(anchor: Date, days: number): Date {
  const p = wallClockParts(anchor, DIGEST_TZ);
  return zonedTimeToUtc(p.year, p.month, p.day + days, DIGEST_HOUR, DIGEST_MINUTE, DIGEST_TZ);
}

// Clock-skew safety margin for the completed-window anchor. A trigger firing
// this close to (but before) a Friday 9:30 AM PT cutoff is treated as if the
// cutoff had already passed. Same-PT-day handling below already covers the
// whole Friday-before-9:30 case; this only guards odd sub-day skew.
const ANCHOR_SNAP_GRACE_MS = 6 * 60 * 60 * 1000;

// The Friday 9:30 AM PT that ENDS the completed window.
//
// Root cause the digest fired for the wrong week: `lastDigestAnchor` rolls back
// a full week whenever it's Friday but before 9:30 AM PT, so a slightly-early
// scheduler run or a manual same-day dispatch reported the PREVIOUS week. Here
// we snap FORWARD to that Friday's 9:30 cutoff when the run happens on the same
// PT day as the upcoming cutoff (i.e. it's Friday, pre-9:30) or within a small
// clock-skew grace of it — so the digest covers "the week ending that Friday".
// Off-day (mid-week) runs fall through to `prev`, i.e. the most recently
// completed Friday week, unchanged. DST-correctness comes from zonedTimeToUtc.
function resolveCompletedAnchor(now: Date): Date {
  const prev = lastDigestAnchor(now);
  const next = shiftAnchor(prev, 7);
  const samePtDayAsNext = isoDateInTz(now, DIGEST_TZ) === isoDateInTz(next, DIGEST_TZ);
  const withinGrace = next.getTime() - now.getTime() <= ANCHOR_SNAP_GRACE_MS;
  return samePtDayAsNext || withinGrace ? next : prev;
}

export type DigestWindow = "completed" | "current";

// completed: [anchor − 7d, anchor)   — what the Friday scheduled run sends. The
//            anchor is resolved so an on/near-cutoff run lands on the correct
//            (that-Friday) window instead of rolling back a full week.
// current:   [anchor, anchor + 7d)   — the in-progress window (since the last
//            Friday 9:30 AM PT), for preview / forced test sends.
export function digestRange(now: Date = new Date(), window: DigestWindow = "completed"): WeekRange {
  if (window === "completed") {
    const anchor = resolveCompletedAnchor(now);
    const start = shiftAnchor(anchor, -7);
    return { start, end: anchor, weekStartIso: isoDateInTz(start, DIGEST_TZ) };
  }
  const anchor = lastDigestAnchor(now);
  const end = shiftAnchor(anchor, 7);
  return { start: anchor, end, weekStartIso: isoDateInTz(anchor, DIGEST_TZ) };
}

// The Friday→Friday window (anchored at 9:30 AM PT) that CONTAINS `date`: it
// starts at the most recent Friday 9:30 AM PT at or before `date` and ends at
// the next Friday 9:30 AM PT cutoff. Used to bucket a changelog entry into the
// same window the digest would have covered it in.
export function digestWeekForDate(date: Date): WeekRange {
  return digestRange(date, "current");
}

// "Mon D – Mon D" in PT (matches the Slack digest header). No year — callers
// that need year-disambiguation format their own label off `range.start/.end`.
export function formatRangeLabel(range: WeekRange): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: DIGEST_TZ });
  return `${fmt(range.start)} – ${fmt(range.end)}`;
}
